import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './VerticalVideoFeed.css';

function VerticalVideoFeed({ videosData }) {
  // Sort videos by date descending (most recent first)
  const sortedVideos = [...videosData].sort(
	(a, b) => new Date(b.date) - new Date(a.date)
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const videosWrapperRef = useRef(null);
  const dragData = useRef({
	startY: 0,
	distance: 0,
	isDragging: false,
	lastTouch: 0,
  });

  // Move to next/previous video
  const handleVideoTransition = (newIndex) => {
	if (isTransitioning) return;
	if (newIndex >= 0 && newIndex < sortedVideos.length) {
	  setIsTransitioning(true);
	  videosWrapperRef.current?.classList.add('transitioning');
	  setCurrentIndex(newIndex);
	  setTimeout(() => {
		setIsTransitioning(false);
		videosWrapperRef.current?.classList.remove('transitioning');
	  }, 300);
	}
  };

  const handleStart = (e) => {
	if (isTransitioning) return;

	const y = e.type === 'mousedown' ? e.clientY : e.touches[0].clientY;
	dragData.current = {
	  startY: y,
	  distance: 0,
	  isDragging: true,
	  lastTouch: Date.now(),
	};

	if (videosWrapperRef.current) {
	  videosWrapperRef.current.style.transition = 'none';
	  videosWrapperRef.current.classList.remove('transitioning');
	}
  };

  const handleMove = (e) => {
	if (!dragData.current.isDragging || isTransitioning) return;

	const y = e.type === 'mousemove' ? e.clientY : e.touches[0].clientY;
	const deltaY = y - dragData.current.startY;

	// Add slight "resistance" if user drags at boundary
	if (
	  (currentIndex === 0 && deltaY > 0) ||
	  (currentIndex === sortedVideos.length - 1 && deltaY < 0)
	) {
	  videosWrapperRef.current?.classList.add('at-boundary');
	  dragData.current.distance = deltaY * 0.2;
	} else {
	  videosWrapperRef.current?.classList.remove('at-boundary');
	  dragData.current.distance = deltaY;
	}

	if (videosWrapperRef.current) {
	  const translateY =
		-(currentIndex * 100) + (dragData.current.distance / window.innerHeight) * 100;
	  videosWrapperRef.current.style.transform = `translateY(${translateY}vh)`;
	}
  };

  const handleEnd = () => {
	if (!dragData.current.isDragging || isTransitioning) return;

	const distance = dragData.current.distance;
	const velocity = Math.abs(distance) / (Date.now() - dragData.current.lastTouch);
	const threshold = window.innerHeight * (velocity > 0.5 ? 0.1 : 0.2);

	if (videosWrapperRef.current) {
	  videosWrapperRef.current.classList.add('transitioning');

	  if (Math.abs(distance) > threshold) {
		if (distance > 0 && currentIndex > 0) {
		  handleVideoTransition(currentIndex - 1);
		} else if (distance < 0 && currentIndex < sortedVideos.length - 1) {
		  handleVideoTransition(currentIndex + 1);
		} else {
		  videosWrapperRef.current.style.transform = `translateY(${
			-currentIndex * 100
		  }vh)`;
		}
	  } else {
		videosWrapperRef.current.style.transform = `translateY(${
		  -currentIndex * 100
		}vh)`;
	  }

	  videosWrapperRef.current.classList.remove('at-boundary');
	}

	dragData.current.isDragging = false;
	dragData.current.distance = 0;
  };

  const handleWheel = (e) => {
	e.preventDefault();
	if (isTransitioning) return;

	const now = Date.now();
	if (now - dragData.current.lastTouch < 100) return;
	dragData.current.lastTouch = now;

	const delta = e.deltaY;
	if (delta > 30 && currentIndex < sortedVideos.length - 1) {
	  handleVideoTransition(currentIndex + 1);
	} else if (delta < -30 && currentIndex > 0) {
	  handleVideoTransition(currentIndex - 1);
	}
  };

  // Attach global listeners
  useEffect(() => {
	const handleMouseUp = () => handleEnd();
	const handleMouseMove = (e) => handleMove(e);
	const handleTouchMove = (e) => {
	  if (dragData.current.isDragging) {
		e.preventDefault();
	  }
	};

	window.addEventListener('mousemove', handleMouseMove);
	window.addEventListener('mouseup', handleMouseUp);
	window.addEventListener('touchmove', handleTouchMove, { passive: false });

	return () => {
	  window.removeEventListener('mousemove', handleMouseMove);
	  window.removeEventListener('mouseup', handleMouseUp);
	  window.removeEventListener('touchmove', handleTouchMove);
	};
  }, [currentIndex, isTransitioning]);

  // Snap to currentIndex when it changes
  useEffect(() => {
	if (videosWrapperRef.current) {
	  videosWrapperRef.current.classList.add('transitioning');
	  videosWrapperRef.current.style.transform = `translateY(${
		-currentIndex * 100
	  }vh)`;
	}
  }, [currentIndex]);

  return (
	<div className="video-feed-container">
	  <div
		className="videos-wrapper"
		ref={videosWrapperRef}
		onTouchStart={handleStart}
		onTouchMove={handleMove}
		onTouchEnd={handleEnd}
		onMouseDown={handleStart}
		onWheel={handleWheel}
	  >
		{sortedVideos.map((video, index) => (
		  <div
			key={index}
			className="video-item"
			style={{ top: `${index * 100}vh` }}
		  >
			{/* Iframe with autoplay if it's the current video */}
			<iframe
			  src={`${video.embedUrl}?autoplay=${index === currentIndex ? 1 : 0}&mute=1&controls=1`}
			  title={`Video ${index + 1}`}
			  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
			  referrerPolicy="strict-origin-when-cross-origin"
			  allowFullScreen
			/>

			{/* Meta info: date + category button (which navigates to /category/:slug) */}
			<div className="video-meta">
			  <span className="video-date">
				{new Date(video.date).toLocaleDateString()}
			  </span>
			  <Link
				to={`/category/${video.category.toLowerCase().replace(/\s+/g, '-')}`}
				className="category-button"
			  >
				{video.category}
			  </Link>
			</div>
		  </div>
		))}
	  </div>

	  <div className="swipe-indicator">Swipe or scroll to see more</div>
	</div>
  );
}

export default VerticalVideoFeed;